/**
 * 新版本登录库
 * @author colynchen
 * @created 2019-03-20
 */
import { invoke } from '@tencent/vft-atom/lib/tenvideo';

import cookie, { parseCookieString } from '../cookie';
import { isNeedUseCookieProxy } from '../CookieProxy';
import env from '../env';
import { request } from '../request';
import { buildUrl } from '../url';
import { getUrlParam, loadScript } from '../util.esm';
import {
  clearCookie,
  getDomain,
  getSkey,
  getUin,
  loginType,
  requestQQLogin,
  requestQQNativeAuth,
  syncCookies,
} from './lib';
import * as reporter from './report';

const loginConfig = {};
/**
 * 支持多业务登录
 * @param {object} params 应用参数
 * @param {number} params.qqAppid QQ登录的appid
 * @param {number} params.wxAppid 微信登录的appid
 * @param {number} params.title   登录页标题
 */
export function setConfig({ qqAppid, wxAppid, title } = {}) {
  loginConfig.qqAppid = qqAppid;
  loginConfig.wxAppid = wxAppid;
  loginConfig.title = title;
}

/**
 * 是否已经登录
 * 不仅单纯仅判断是否有 main_login 就代表已登录
 * 通过 cookie 中是否存在登录态对应的存在关键信息来决定
 * @param {String} type 可获取指定登录态类型的登录态
 */
export function isLogin(type) {
  if (!type) type = loginType();

  const vuserid = isNewQQLogin() ? cookie.get('vqq_vuserid') : cookie.get('vuserid');
  const vusession = isNewQQLogin() ? cookie.get('vqq_vusession') : cookie.get('vusession');

  // cookie 中的 type 可能返回 none
  if (!type || type === 'none' || !vuserid || !vusession) {
    // 不存在视频票据时未登录
    return false;
  }

  if (type === 'wx') {
    return isWXLogin();
  } if (type === 'qq') {
    return isQQLogin();
  }

  return true;
}

/**
 * 判断是否微信登录态
 */
export function isWXLogin() {
  if (loginType() === 'wx' && cookie.get('appid') && cookie.get('openid') && cookie.get('access_token')) {
    return true;
  }
  return false;
}

/**
 * 判断是否 QQ 登录态
 */
export function isQQLogin() {
  return isPTLogin() || isNewQQLogin();
}

/**
 * 是否是旧版 PTLogin 登录态
 */
export function isPTLogin() {
  if (loginType() === 'wx') return false;
  if (!isNewQQLogin() && ((cookie.get('uin') && cookie.get('skey'))
    || (cookie.get('luin') && cookie.get('lskey')))) {
    return true;
  }
  return false;
}

/**
 * 是否是新的互联登录态
 */
export function isNewQQLogin() {
  if (loginType() === 'wx') return false;
  if (loginType() === 'qq' && cookie.get('vqq_appid') && cookie.get('vqq_openid') && cookie.get('vqq_access_token')) {
    return true;
  }
  return false;
}

/**
 * 校验强登录态
 * @param {Function} callback
 */
export function isStrictLogin(callback) {
  if (typeof callback !== 'function') return;
  if (!isLogin()) {
    return callback(false);
  }

  const params = {};
  if (loginType() === 'wx') {
    params.type = 3;
    params.appid = cookie.get('appid');
    params.openid = cookie.get('openid');
    params.access_token = cookie.get('access_token');
  } else if (isNewQQLogin()) { // 新的QQ互联登录态
    params.type = 6;
    params.vqq_appid = cookie.get('vqq_appid');
    params.vqq_openid = cookie.get('vqq_openid');
    params.vqq_access_token = cookie.get('vqq_access_token');
  } else if (isPTLogin()) {
    params.type = 1;
    params.uin = getUin();
    params.skey = getSkey();
  }

  checkUser(params).then(() => {
    callback(true);
  })
    .catch(() => {
      callback(false);
    });
}

/**
 * 检验账号登录态、账号一致性等
 * @example
 * 1、qq强登录校验 {type:1,uin,'',skey:''}
 * 2、微信登录态校验 {type:3,appid:'',openid:'',access_token:''}
 * 3、微信账号一致性校验 {type:4,appid1:'',openid1:'',appid2:'',openid2:''}
 * 4、QQ互联登录态校验 {type:6,vqq_appid:'',vqq_openid:'',vqq_access_token:''}
 * 5、QQ登录态一致性校验 {type:7,appid1:'',openid1:'',appid2:'',openid2:''}
 * 6、QQ号与互联登录态一致性校验 {type:7,appid1:'',openid1:'',uin:''}
 */
export function checkUser(params, onSuccess, onFail) {
  return new Promise((resolve, reject) => {
    if (typeof onSuccess !== 'function') {
      onSuccess = resolve;
      onFail = reject;
    }
    return request('//vip.video.qq.com/fcgi-bin/comm_cgi?name=check_login&otype=xjson', params, {
      timeout: 60000,
    }).then((res) => {
      if (res && res.result && res.result.code === 0) {
        onSuccess && onSuccess(res);
      } else {
        onFail && onFail(res);
      }
    })
      .catch((e) => {
        onFail && onFail(e);
      });
  });
}

/**
 * 退出登录
 */
export function logout(callback) {
  if (env.isApp) {
    invoke('logout', {
      needBack: false,
    }, (jsonTxt) => {
      try {
        const res = JSON.parse(jsonTxt);
        if (res && res.errCode === 0) {
          callback && callback(true);
        } else {
          callback && callback(false);
        }
      } catch (e) {
        callback && callback(false);
      }
    });
  } else {
    clearCookie(() => {
      callback && callback(true);
    });
  }
  reporter.report({
    f_event: reporter.EVENT_INIT_LOGOUT,
  });
}

/**
 * 拉登录
 * @param {Object} param 登录参数
 * @param {Object} param.type 登录类型，qq/wx
 * @param {Object} param.url 登录回跳地址
 * @param {Boolean} clear 是否清除登录态
 */
export function openLogin(param = { type: '' }, clear = false) {
  if (typeof param === 'string') {
    param = {
      type: param,
    };
  } else if (Object.prototype.toString.call(param) !== '[object Object]') {
    param = {};
  }
  if (!param.url) {
    param.url = location.href;
  }

  reporter.report({
    f_event: reporter.EVENT_INIT_LOGIN,
    f_type: param.type,
  });

  // 存在mpcookie，认为是来自于腾讯视频小程序，同步登录态；其他小程序不做登录态同步，直接走h5逻辑
  if (env.isWeChatMiniProgram && getUrlParam('mpcookie')) {
    loadScript(
      'jweixin',
      '//res.wx.qq.com/open/js/jweixin-1.3.2.js',
      () => {
        // eslint-disable-next-line
        wx.miniProgram.navigateTo({
          url: `/preload_vip/login/index?isLogin=${isLogin()}`,
        });
      },
    );
  } else if (env.isApp) {
    invoke('actionLogin', {
      type: param.type || 'tv',
    }).then((res) => {
      if (res && res.type) { // 登录或者退出登录成功
        location.href = param.url;
      }
    });
  } else {
    if (param.type === 'wx') {
      param.back = 1;
    }
    if (clear) {
      logout(_doLogin.bind(this, param));
    } else {
      _doLogin(param);
    }
  }
}

/**
 * 双登录页登录
 * @param {Object} param 登录参数
 */
function _doLogin(param) {
  if (param.url) {
    param.ru = param.url;
    delete param.url;
  }
  // 清除上次续期的时间
  cookie.remove('last_refresh_time', {
    domain: getDomain(),
    path: '/',
  });
  cookie.remove('last_refresh_vuserid', {
    domain: getDomain(),
    path: '/',
  });
  location.href = buildUrl('//film.qq.com/weixin/login.html', {
    qqAuth: getUrlParam('qqAuth'),
    ...loginConfig,
    ...param,
  }, true);
}

/**
 * 获取主登录态用户信息
 * @returns {(object|null)}
 */
export function getMainUserInfo() {
  if (!isLogin()) return Promise.resolve(null);

  return Promise.resolve({
    type: loginType(),
    nick: getNick(),
    headurl: getAvatar(),
    id: getUin() || getUid(),
  });
}

/**
 * 获取主账号 cookie 信息
 * @returns {(object|null)}
 */
export function getMainCookie() {
  if (isNeedUseCookieProxy() && cookie.isProxyEnabled()) {
    return cookie.getCookies();
  }
  const _cookies = parseCookieString(document.cookie);
  return _cookies;
}

/**
 * 获取用户昵称
 * @returns {string}
 */
function getNick() {
  if (!isLogin()) return '';
  const nick = cookie.get(`${loginType()}_nick`);
  try {
    return decodeURIComponent(nick);
  } catch (error) {
    return nick;
  }
}

/**
 * 获取用户头像
 * @returns {string}
 */
function getAvatar() {
  if (!isLogin()) return '';
  try {
    return decodeURIComponent(cookie.get(`${loginType()}_head`));
  } catch (e) {
    return '';
  }
}

/**
 * 获取登录账号 id
 * @returns {string|number}
 */
export function getUid() {
  const type = loginType();
  if (!type) return '';
  if (type === 'qq') {
    return isNewQQLogin() ? cookie.get('vqq_openid') : getUin();
  }
  return cookie.get('openid');
}

/**
 * 获取视频 vuserid
 * @returns {string}
 */
export function getVuid() {
  return isNewQQLogin() ? cookie.get('vqq_vuserid') : cookie.get('vuserid');
}

/**
 * QQLogin openid,access_token换视频票据
 */
export function openNativeLogin() {
  reporter.report({
    f_event: reporter.EVENT_LOGIN_START,
  });
  return requestQQNativeAuth()
    .then(params => requestQQLogin(params)
      .then(() => new Promise((resolve) => {
        syncCookies(resolve);
      })));
}
